import { Select, SelectOptionType, useSelectDefaultGroupHandler } from '@recruit-robin/robin-theme/web-components';
import { ChevronDown, ChevronUp, Users_01 } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SupportedLanguage } from 'shared/legacy/contexts/LocalizationContext/types';
import { JobFormValues } from '../../EditEnrichedVacancy.types';

type Option = {
  value: string;
  label: Record<string, string> | string;
};

type CustomSelectFormProps<T extends Record<string, any>> = {
  control: Control<T>;
  fieldName: string;
  options: Option[];
  label: string;
  placeholder?: string;
  multiple?: boolean;
  limit?: boolean;
  selectAll?: boolean;
  selectPartial?: {
    label: string;
    icon: any;
    values: string[];
  };
  customizeSelectOption?: (option: SelectOptionType<string>, defaultProps: any) => any;
};

export const CustomSelectForm = <T extends Record<string, any>>({
  control,
  fieldName,
  options,
  label,
  placeholder,
  multiple = false,
  limit = false,
  selectAll = false,
  selectPartial,
  customizeSelectOption,
}: CustomSelectFormProps<T>) => {
  const { dictionary, language } = useContext(LocalizationContext);
  const { watch } = useFormContext<JobFormValues>();

  const [search, setSearch] = useState('');

  const value = watch(fieldName as any);

  const selectedOptionObjects = (Array.isArray(value) ? value : value ? [value] : [])
    .map((val) => options.find((opt) => opt.value === val))
    .filter((opt): opt is Option => opt !== undefined)
    .map((opt) => ({
      value: opt.value,
      label: typeof opt.label === 'string' ? opt.label : opt.label[language as SupportedLanguage],
    }));

  const selectGroupHandler = useSelectDefaultGroupHandler({
    search,
    value: selectedOptionObjects,
    options: options.map((option) => ({
      value: option.value,
      label: typeof option.label === 'string' ? option.label : option.label[language],
    })),
    setValue: (newValue) => {
      control.setValue(fieldName as any, newValue?.map((opt) => opt.value) as any);
    },
    limit: limit as true,
    onRenderLimitValue: ({ length }) => `${length} ${label}`,
    selectAll: selectAll ? { icon: Users_01, label: dictionary.selectAll } : undefined,
    selectPartial,
  });

  return (
    <Select
      value={selectedOptionObjects}
      options={selectGroupHandler.options}
      renderOptions={selectGroupHandler.renderOptions}
      renderValue={selectGroupHandler.renderValue}
      onChange={(newValue) => {
        control.setValue(fieldName as any, newValue?.map((opt) => opt.value) as any);
      }}
      multiple={multiple as true}
      closeOnClickOutside
      placeholder={placeholder || label}
      text={search}
      onTextChange={setSearch}
      customizeValuePill={(opt) => {
        const props = selectGroupHandler.customizeValuePill(opt);

        return { ...props, type: 'subtle1' };
      }}
      customizeSelectOption={customizeSelectOption || selectGroupHandler.customizeSelectedOption}
      endIcon={({ visible }) => (
        <div onClick={(e) => e.stopPropagation()}>{visible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
      )}
    />
  );
};
